import React, { useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import DocCard from 'components/Cards/DocCard'
import UploadPreviewDialog from 'components/UploadPreviewDialog'
import EditPreviewDialog from 'components/EditPreviewDialog'
import Spinner from 'components/Spinner'
import { useDocListData } from 'hooks/useDocListData'
import { useDocListStore } from 'store/useDocListStore'
import DocUploadLayout from 'layouts/DocUploadLayout'
import {
    VECHILE_CONTAINER_WIDTH,
    NEW_RES_ACCORDIAN_WIDTH,
    DOCUMENT_UPLOAD_CONTAINER,
    DOCUMENT_UPLOAD_SM_CONTAINER
} from 'constants/layoutConstant'
import locationIcon from './../images/location.svg'
import { MESSAGES } from '../messages/messages'
import { docUploadAnalytics } from 'analytics/docUploadAnalytics'

const DocUploadPage = () => {
    let listKey = Date.now()
    const { isLoading, isFetching } = useDocListData()
    const { docs: docList, dealerName } = useDocListStore((state) => state)
    const docToDisplay = docList?.length > 0
    const analyticsLoaded = useRef(false)

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        if (!isLoading && !isFetching && !analyticsLoaded.current) {
            analyticsLoaded.current = true
            docUploadAnalytics.onLoad()
        }
    }, [isLoading, isFetching])

    if (isLoading || isFetching) {
        return <Spinner />
    }

    return (
        <DocUploadLayout theme="light">
            {docToDisplay ? (
                <Box
                    sx={{
                        backgroundColor: 'common.white.main',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 6, smd: 12, md: 22 },
                        px: { xs: 4, md: 0 },
                        width: '100%'
                    }}
                >
                    <Typography
                        data-testid="document_upload_title"
                        className="document_upload_clss"
                        sx={{
                            color: 'common.charcoal.main',
                            typography: { xs: 'smallHeadline4', smd: 'smallHeadline3', md: 'smallHeadline0' },
                            fontWeight: 600
                        }}
                    >
                        {MESSAGES.document_upload}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14,
                            maxWidth: {
                                xs: DOCUMENT_UPLOAD_CONTAINER,
                                smd: DOCUMENT_UPLOAD_SM_CONTAINER,
                                md: DOCUMENT_UPLOAD_CONTAINER
                            },
                            width: '100%',
                            ml: { xs: 0, smd: 25, md: 27.5 }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <Typography
                                data-testid="title"
                                sx={{
                                    fontWeight: 'regular',
                                    color: 'common.charcoal.main',
                                    typography: {
                                        xs: 'regularSubTitle',
                                        smd: 'regularTitle',
                                        md: 'smallHeadline3'
                                    }
                                }}
                            >
                                {MESSAGES.title}
                            </Typography>

                            <Box
                                data-testid="sub-heading"
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: { xs: 'regularBody2', smd: 'regularBody1' }
                                }}
                            >
                                {MESSAGES.sub_heading_one}&nbsp;
                                {MESSAGES.sub_heading_two}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4
                                }}
                            >
                                <Typography
                                    data-testid="documents_requested_by"
                                    component={'span'}
                                    sx={{
                                        typography: 'boldBody2',
                                        color: 'common.charcoal.main'
                                    }}
                                >
                                    {MESSAGES.documents_requested_by}
                                </Typography>
                                <Typography
                                    component="span"
                                    className="text-with-underline-item"
                                    color="common.charcoal.main"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        gap: 1,
                                        typography: 'regularBody2'
                                    }}
                                >
                                    <img
                                        src={locationIcon}
                                        data-testid="location_Icon"
                                        alt={'location Icon'}
                                        width={10}
                                        height={13}
                                    />
                                    <Typography
                                        component="span"
                                        data-testid="dealer-name"
                                        sx={{
                                            typography: 'regularBody2'
                                        }}
                                    >
                                        {dealerName}
                                    </Typography>
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                mb: 6
                            }}
                        >
                            <Box
                                data-testid="doc_cards_block"
                                sx={{
                                    display: 'grid',
                                    gap: 4,
                                    maxWidth: { xs: 434, md: 600 },
                                    width: '100%'
                                }}
                            >
                                {docList.map((docItem, index) => (
                                    <DocCard key={`${index + listKey}`} {...docItem} />
                                ))}
                            </Box>
                            <Box data-testid="warning" sx={{ maxWidth: { md: 600 }, width: '100%' }}>
                                <Typography
                                    data-testid="max_file_size_and_types_warning"
                                    sx={{
                                        color: 'common.charcoal.main',
                                        typography: 'regularBody2'
                                    }}
                                >
                                    {MESSAGES.max_file_size_and_types}
                                </Typography>
                            </Box>
                            <UploadPreviewDialog />
                            <EditPreviewDialog />
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        backgroundColor: 'common.white.main',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        px: { xs: 3, md: 0 },
                        gap: { xs: 6, smd: 12, md: 22 },
                        width: '100%'
                    }}
                >
                    <Typography
                        data-testid="document_upload_title"
                        sx={{
                            color: 'common.charcoal.main',
                            typography: { xs: 'smallHeadline4', md: 'smallHeadline0' },
                            fontWeight: 600
                        }}
                    >
                        {MESSAGES.document_upload}
                    </Typography>
                    <Box
                        sx={{
                            maxWidth: {
                                xs: 'initial',
                                smd: NEW_RES_ACCORDIAN_WIDTH,
                                md: VECHILE_CONTAINER_WIDTH
                            },
                            margin: 'auto',
                            width: '100%',
                            mb: 18
                        }}
                    >
                        <Box
                            sx={{
                                maxWidth: { xs: 400, md: 580 },
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', smd: 'row' },
                                    gap: { xs: 1, smd: 1.5 }
                                }}
                            >
                                <Typography
                                    data-testid="no_doc_title"
                                    sx={{
                                        color: 'common.charcoal.main',
                                        typography: {
                                            xs: 'regularSubTitle',
                                            smd: 'smallHeadline4',
                                            md: 'smallHeadline3'
                                        }
                                    }}
                                >
                                    {MESSAGES.no_doc}
                                </Typography>
                            </Box>

                            <Typography
                                data-testid="no_doc_content"
                                component={'span'}
                                sx={{
                                    color: 'common.charcoal.main',
                                    typography: 'regularBody1'
                                }}
                            >
                                {MESSAGES.no_doc_sub}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
        </DocUploadLayout>
    )
}

export default DocUploadPage
